import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { baseurl } from '../../Baseurl'
import axiosInstance from '../../api/axiosInstance'

/* ─── Design tokens (same as Security.jsx) ─────────────────────────────────── */
const COLORS = {
  gray:   '#5B6B79',
  blue:   '#96C5F7',
  red:    '#9E2B25',
  orange: '#F29E4C',
  green:  '#2CA87F',
  purple: '#8B5CF6',
}

const thStyle = {
  fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase',
  letterSpacing: '0.05em', padding: '8px 12px', background: '#f8f9fb',
  borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap',
}
const tdStyle = {
  fontSize: 13, color: '#2d3748', padding: '9px 12px',
  verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5',
}

const TABS = [
  { id: 'email', label: 'Email Templates', icon: 'ti-mail',    color: COLORS.blue   },
  { id: 'sms',   label: 'SMS Templates',   icon: 'ti-message', color: COLORS.green  },
]

/* ─── Shared small components ───────────────────────────────────────────────── */
const IconBox = ({ icon, color }) => (
  <div style={{
    width: 32, height: 32, borderRadius: 8,
    background: `${color}20`, display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }}>
    <i className={`ti ${icon}`} style={{ color, fontSize: 16 }} />
  </div>
)

const BackBtn = ({ onClick, title = 'Go back' }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0',
      background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 15, marginRight: 4, flexShrink: 0,
    }}
    onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue }}
    onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray }}
  >
    <i className="ti ti-arrow-left" />
  </button>
)

const CloseBtn = ({ onClick }) => (
  <button onClick={onClick} style={{
    marginLeft: 'auto', width: 26, height: 26, borderRadius: 6,
    border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: COLORS.gray, fontSize: 14,
  }}>
    <i className="ti ti-x" />
  </button>
)

const ActionIconBtn = ({ icon, color, title, onClick }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer',
      background: `${color}18`, color, display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center', fontSize: 14,
      transition: 'background 0.15s', marginRight: 4,
    }}
    onMouseEnter={e => e.currentTarget.style.background = color + '35'}
    onMouseLeave={e => e.currentTarget.style.background = color + '18'}
  >
    <i className={`ti ${icon}`} />
  </button>
)

/* ─── Rich text editor ──────────────────────────────────────────────────────── */
const RichEditor = ({ value, onChange }) => {
  const ref = useRef(null)

  // Sync external value into the div only on mount / when id changes
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || ''
    }
  }, []) // intentionally only on mount

  const exec = (cmd, val = null) => { document.execCommand(cmd, false, val); ref.current?.focus() }

  const toolbarBtn = (cmd, icon, title, val = null) => (
    <button
      key={cmd + (val || '')}
      onMouseDown={e => { e.preventDefault(); exec(cmd, val) }}
      title={title}
      style={{
        width: 28, height: 28, border: '1px solid #dde3eb', borderRadius: 5,
        background: '#fff', cursor: 'pointer', display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', fontSize: 13, color: COLORS.gray,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.borderColor = COLORS.blue }}
      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#dde3eb' }}
    >
      <i className={`ti ${icon}`} />
    </button>
  )

  return (
    <div style={{ border: '1.5px solid #dde3eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '6px 8px', borderBottom: '1px solid #e8ecf0', background: '#f8f9fb', alignItems: 'center' }}>
        {toolbarBtn('bold',          'ti-bold',          'Bold')}
        {toolbarBtn('italic',        'ti-italic',        'Italic')}
        {toolbarBtn('underline',     'ti-underline',     'Underline')}
        {toolbarBtn('strikeThrough', 'ti-strikethrough', 'Strikethrough')}

        <div style={{ width: 1, height: 20, background: '#dde3eb', margin: '0 2px' }} />

        {toolbarBtn('justifyLeft',   'ti-align-left',    'Align Left')}
        {toolbarBtn('justifyCenter', 'ti-align-center',  'Align Center')}
        {toolbarBtn('justifyRight',  'ti-align-right',   'Align Right')}

        <div style={{ width: 1, height: 20, background: '#dde3eb', margin: '0 2px' }} />

        {toolbarBtn('insertUnorderedList', 'ti-list',        'Bullet List')}
        {toolbarBtn('insertOrderedList',   'ti-list-numbers','Numbered List')}

        <div style={{ width: 1, height: 20, background: '#dde3eb', margin: '0 2px' }} />

        {/* Font size */}
        <select
          onMouseDown={e => e.stopPropagation()}
          onChange={e => { exec('fontSize', e.target.value); e.target.value = '' }}
          defaultValue=""
          title="Font Size"
          style={{ height: 28, fontSize: 12, border: '1px solid #dde3eb', borderRadius: 5, padding: '0 4px', background: '#fff', color: COLORS.gray, cursor: 'pointer' }}
        >
          <option value="" disabled>Size</option>
          {[1,2,3,4,5,6,7].map(s => <option key={s} value={s}>{[10,12,14,16,18,24,32][s-1]}px</option>)}
        </select>

        {/* Font family */}
        <select
          onMouseDown={e => e.stopPropagation()}
          onChange={e => { exec('fontName', e.target.value); e.target.value = '' }}
          defaultValue=""
          title="Font"
          style={{ height: 28, fontSize: 12, border: '1px solid #dde3eb', borderRadius: 5, padding: '0 4px', background: '#fff', color: COLORS.gray, cursor: 'pointer' }}
        >
          <option value="" disabled>Font</option>
          {['Arial','Georgia','Times New Roman','Courier New','Verdana','Trebuchet MS'].map(f => <option key={f} value={f}>{f}</option>)}
        </select>

        <div style={{ width: 1, height: 20, background: '#dde3eb', margin: '0 2px' }} />

        {/* Text color */}
        <label title="Text Color" style={{ position: 'relative', width: 28, height: 28, border: '1px solid #dde3eb', borderRadius: 5, background: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="ti ti-letter-a" style={{ fontSize: 14, color: COLORS.gray }} />
          <input type="color" defaultValue="#000000"
            onChange={e => exec('foreColor', e.target.value)}
            style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }} />
        </label>

        {/* Highlight color */}
        <label title="Highlight Color" style={{ position: 'relative', width: 28, height: 28, border: '1px solid #dde3eb', borderRadius: 5, background: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="ti ti-highlight" style={{ fontSize: 14, color: COLORS.orange }} />
          <input type="color" defaultValue="#ffff00"
            onChange={e => exec('hiliteColor', e.target.value)}
            style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }} />
        </label>

        <div style={{ width: 1, height: 20, background: '#dde3eb', margin: '0 2px' }} />

        {toolbarBtn('removeFormat', 'ti-clear-formatting', 'Clear Formatting')}
      </div>

      {/* Editable area */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current?.innerHTML || '')}
        style={{
          minHeight: 140, padding: '10px 12px', fontSize: 13, color: '#2d3748',
          outline: 'none', lineHeight: 1.6,
        }}
      />
    </div>
  )
}

/* ─── Email template list (inline, per category) ────────────────────────────── */
const EmailTemplateListView = ({ category, onBack }) => {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading]     = useState(false)
  const [showAdd, setShowAdd]     = useState(false)
  const [newData, setNewData]     = useState({ template_name: '', subject: '', content: '' })
  const [editData, setEditData]   = useState({}) // { [id]: { subject, content } }

  const fetchTemplates = async () => {
    if (!category?.id) return
    setLoading(true)
    try {
      const { data } = await axiosInstance.get(`getEmailTemplatesByCategory/${category.id}`)
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : Array.isArray(data?.templates) ? data.templates : []
      setTemplates(list)
      const initial = {}
      list.forEach(t => { initial[t.id] = { subject: t.subject || '', content: t.content || '' } })
      setEditData(initial)
    } catch { setTemplates([]) }
    finally { setLoading(false) }
  }

  useEffect(() => { fetchTemplates() }, [category])

  const handleUpdate = async (t) => {
    const d = editData[t.id] || {}
    try {
      await axiosInstance.put(`updateEmailTemplate/${t.id}`, { subject: d.subject, content: d.content })
      window.alert('Template saved successfully.')
    } catch (e) { window.alert(e.response?.data?.message || 'Failed to save template.') }
  }

  const handleCreate = async () => {
    if (!newData.template_name.trim() || !newData.subject.trim() || !newData.content.trim()) {
      window.alert('Template name, subject, and content are required.'); return
    }
    try {
      await axiosInstance.post('createEmailTemplate', { sms_category_id: category.id, ...newData })
      setShowAdd(false); setNewData({ template_name: '', subject: '', content: '' }); fetchTemplates()
    } catch (e) { window.alert(e.response?.data?.message || 'Failed to create template.') }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return
    try {
      await axiosInstance.delete(`deleteEmailTemplate/${id}`)
      fetchTemplates()
    } catch (e) { window.alert(e.response?.data?.message || 'Failed to delete template.') }
  }

  const changeEdit = (id, field, value) =>
    setEditData(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }))

  return (
    <div>
      {/* Sub-header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', borderBottom: '1px solid #e8ecf0', background: '#fafbfc' }}>
        <BackBtn onClick={onBack} title="Back to categories" />
        <IconBox icon="ti-mail" color={COLORS.blue} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>Email Templates</div>
          <div style={{ fontSize: 11, color: COLORS.gray }}>{category?.category_name}</div>
        </div>
        <button
          className="bgBtn btn-sm d-flex align-items-center gap-1"
          style={{ fontSize: 12, padding: '4px 12px', marginLeft: 'auto' }}
          onClick={() => setShowAdd(true)}
        >
          <i className="ti ti-plus" style={{ fontSize: 14 }} /> New Template
        </button>
      </div>

      <div style={{ padding: '16px 20px' }}>
        {loading && <div style={{ color: COLORS.gray, fontSize: 13 }}>Loading templates...</div>}
        {!loading && templates.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: COLORS.gray }}>
            <i className="ti ti-mail-off" style={{ fontSize: 28, display: 'block', marginBottom: 6, opacity: 0.4 }} />
            No templates for this category
          </div>
        )}
        {templates.map(t => (
          <div key={t.id} style={{ marginBottom: 14, border: '1px solid #e8ecf0', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{
              padding: '10px 16px', background: '#f8f9fb', borderBottom: '1px solid #e8ecf0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>{t.template_name}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="bgBtn btn-sm d-flex align-items-center gap-1"
                  style={{ fontSize: 12, padding: '4px 12px' }}
                  onClick={() => handleUpdate(t)}
                >
                  <i className="ti ti-device-floppy" style={{ fontSize: 14 }} /> Save
                </button>
                <ActionIconBtn icon="ti-trash" color={COLORS.red} title="Delete" onClick={() => handleDelete(t.id)} />
              </div>
            </div>
            <div style={{ padding: 16 }} className="smsTempText">
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 4 }}>Subject</label>
                <input
                  className="form-control"
                  value={editData[t.id]?.subject || ''}
                  onChange={e => changeEdit(t.id, 'subject', e.target.value)}
                  placeholder="Email subject"
                  style={{ fontSize: 13 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 4 }}>Content</label>
                <RichEditor
                  value={editData[t.id]?.content || ''}
                  onChange={html => changeEdit(t.id, 'content', html)}
                />
              </div>
              <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 6 }}>
                Use placeholders like {'{patientName}'}, {'{date}'}, {'{appointmentId}'}.
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create modal */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowAdd(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: 420, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <IconBox icon="ti-mail" color={COLORS.blue} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>New Email Template</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>{category?.category_name}</div>
              </div>
              <CloseBtn onClick={() => setShowAdd(false)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {[['Template Name', 'template_name', 'e.g. Appointment Confirmation'], ['Subject', 'subject', 'Email subject line']].map(([lbl, field, ph]) => (
                <div key={field}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 4 }}>{lbl}</label>
                  <input className="form-control" style={{ fontSize: 13 }} value={newData[field]}
                    onChange={e => setNewData(p => ({ ...p, [field]: e.target.value }))} placeholder={ph} />
                </div>
              ))}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 4 }}>Content</label>
                <RichEditor
                  value={newData.content}
                  onChange={html => setNewData(p => ({ ...p, content: html }))}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAdd(false)} style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleCreate} className="bgBtn btn-sm" style={{ padding: '7px 16px', fontSize: 13 }}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── SMS template list (inline, per category) ──────────────────────────────── */
const SmsTemplateListView = ({ category, onBack }) => {
  const [templates, setTemplates]         = useState([])
  const [loading, setLoading]             = useState(false)
  const [showAddTemplate, setShowAdd]     = useState(false)
  const [newData, setNewData]             = useState({ template_name: '', subject: '', content: '' })

  const fetch = async () => {
    setLoading(true)
    try {
      const { data } = await axiosInstance.get('getAllSmsTemplates')
      const all = Array.isArray(data) ? data : data?.data || []
      setTemplates(all.filter(t => String(t.sms_category_id) === String(category.id)))
    } catch { setTemplates([]) }
    finally { setLoading(false) }
  }

  useEffect(() => { if (category?.id) fetch() }, [category])

  const handleUpdate = async (t) => {
    try {
      await axiosInstance.put(`updateSmsTemplate/${t.id}`, { sms_category_id: category.id, template_name: t.template_name, subject: t.subject, content: t.content })
      window.alert('Template updated.'); fetch()
    } catch { window.alert('Failed to update.') }
  }

  const handleCreate = async () => {
    try {
      await axiosInstance.post('createSmsTemplate', { sms_category_id: category.id, ...newData })
      window.alert('Template created.'); setShowAdd(false); setNewData({ template_name: '', subject: '', content: '' }); fetch()
    } catch { window.alert('Failed to create.') }
  }

  const change = (id, field, value) => setTemplates(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t))

  return (
    <div>
      {/* Sub-header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', borderBottom: '1px solid #e8ecf0', background: '#fafbfc' }}>
        <BackBtn onClick={onBack} title="Back to categories" />
        <IconBox icon="ti-message" color={COLORS.green} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>SMS Templates</div>
          <div style={{ fontSize: 11, color: COLORS.gray }}>{category?.category_name}</div>
        </div>
        <button
          className="bgBtn btn-sm d-flex align-items-center gap-1"
          style={{ fontSize: 12, padding: '4px 12px', marginLeft: 'auto' }}
          onClick={() => setShowAdd(true)}
        >
          <i className="ti ti-plus" style={{ fontSize: 14 }} /> New Template
        </button>
      </div>

      <div style={{ padding: '16px 20px' }}>
        {loading && <div style={{ color: COLORS.gray, fontSize: 13 }}>Loading templates...</div>}
        {!loading && templates.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: COLORS.gray }}>
            <i className="ti ti-message-off" style={{ fontSize: 28, display: 'block', marginBottom: 6, opacity: 0.4 }} />
            No templates for this category
          </div>
        )}
        {templates.map(t => (
          <div key={t.id} style={{ marginBottom: 14, border: '1px solid #e8ecf0', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{
              padding: '10px 16px', background: '#f8f9fb', borderBottom: '1px solid #e8ecf0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>{t.template_name}</span>
              <button
                className="bgBtn btn-sm d-flex align-items-center gap-1"
                style={{ fontSize: 12, padding: '4px 12px' }}
                onClick={() => handleUpdate(t)}
              >
                <i className="ti ti-device-floppy" style={{ fontSize: 14 }} /> Update
              </button>
            </div>
            <div style={{ padding: 16 }} className="smsTempText">
              <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 4 }}>Content</label>
              <textarea
                className="form-control"
                rows="3"
                value={t.content}
                onChange={e => change(t.id, 'content', e.target.value)}
                style={{ fontSize: 13 }}
              />
              <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 4 }}>SMS content should be short and concise.</div>
            </div>
          </div>
        ))}
      </div>

      {/* Create modal */}
      {showAddTemplate && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowAdd(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: 380, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <IconBox icon="ti-message" color={COLORS.green} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>New SMS Template</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>{category?.category_name}</div>
              </div>
              <CloseBtn onClick={() => setShowAdd(false)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {[['Template Name', 'template_name', 'e.g. Booking Reminder'], ['Subject', 'subject', 'Message Subject']].map(([lbl, field, ph]) => (
                <div key={field}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 4 }}>{lbl}</label>
                  <input className="form-control" style={{ fontSize: 13 }} value={newData[field]}
                    onChange={e => setNewData(p => ({ ...p, [field]: e.target.value }))} placeholder={ph} />
                </div>
              ))}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 4 }}>Content</label>
                <textarea className="form-control" rows="3" style={{ fontSize: 13 }} value={newData.content}
                  onChange={e => setNewData(p => ({ ...p, content: e.target.value }))} placeholder="Message Body" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAdd(false)} style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleCreate} className="bgBtn btn-sm" style={{ padding: '7px 16px', fontSize: 13 }}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Category table panel (shared by Email & SMS) ──────────────────────────── */
const CategoryPanel = ({ accentColor, categories, loading, onView, onEdit, onDelete }) => (
  <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {['ID', 'Category Name', 'Actions'].map((h, i) => (
            <th key={h} style={{ ...thStyle, borderLeft: i === 0 ? `3px solid ${accentColor}` : undefined, textAlign: i === 2 ? 'center' : undefined }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={3} style={{ ...tdStyle, textAlign: 'center', color: COLORS.gray, padding: '32px 0' }}>Loading...</td></tr>
        ) : categories.length === 0 ? (
          <tr>
            <td colSpan={3} style={{ ...tdStyle, textAlign: 'center', color: COLORS.gray, padding: '32px 0' }}>
              <i className="ti ti-database-off" style={{ fontSize: 28, display: 'block', marginBottom: 6, opacity: 0.4 }} />
              No categories found
            </td>
          </tr>
        ) : categories.map((cat, i) => (
          <tr key={cat.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}
            onMouseEnter={e => e.currentTarget.style.background = `${accentColor}0d`}
            onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}
          >
            <td style={{ ...tdStyle, borderLeft: `3px solid ${accentColor}` }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: accentColor, background: `${accentColor}15`, padding: '2px 8px', borderRadius: 6 }}>{cat.id}</span>
            </td>
            <td style={tdStyle}><span style={{ fontWeight: 600 }}>{cat.category_name}</span></td>
            <td style={{ ...tdStyle, textAlign: 'center' }}>
              <ActionIconBtn icon="ti-eye"        color={COLORS.blue}   title="View Templates" onClick={() => onView(cat)} />
              <ActionIconBtn icon="ti-edit"       color={COLORS.orange} title="Edit"           onClick={() => onEdit(cat)} />
              <ActionIconBtn icon="ti-trash"      color={COLORS.red}    title="Delete"         onClick={() => onDelete(cat.id)} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

/* ─── Email tab ─────────────────────────────────────────────────────────────── */
const EmailTab = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading]       = useState(false)
  const [viewCategory, setViewCat]  = useState(null)
  const [newName, setNewName]       = useState('')
  const [editItem, setEditItem]     = useState(null)
  const [showAdd, setShowAdd]       = useState(false)
  const [showEdit, setShowEdit]     = useState(false)

  const fetch = async () => {
    setLoading(true)
    try {
      const { data } = await axiosInstance.get('getAllSmsCategories')
      setCategories(Array.isArray(data?.categories) ? data.categories : [])
    } catch { } finally { setLoading(false) }
  }
  useEffect(() => { fetch() }, [])

  const add = async () => {
    if (!newName.trim()) return
    try { await axiosInstance.post(`${baseurl}addSmsCategory`, { category_name: newName.trim() }); setNewName(''); fetch() } catch { }
  }
  const update = async () => {
    if (!editItem?.category_name?.trim()) return
    try { await axiosInstance.put(`updateSmsCategory/${editItem.id}`, { category_name: editItem.category_name.trim() }); setEditItem(null); setShowEdit(false); fetch() } catch { }
  }
  const del = async (id) => {
    if (!window.confirm('Delete this category?')) return
    try { await axiosInstance.delete(`deleteSmsCategory/${id}`); fetch() } catch { }
  }

  if (viewCategory) return <EmailTemplateListView category={viewCategory} onBack={() => setViewCat(null)} />

  return (
    <div style={{ padding: '14px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize: 13, padding: '5px 12px' }} onClick={() => setShowAdd(true)}>
          <i className="ti ti-plus" style={{ fontSize: 15 }} /> Add Category
        </button>
      </div>
      <CategoryPanel accentColor={COLORS.blue} categories={categories} loading={loading}
        onView={setViewCat} onEdit={c => { setEditItem(c); setShowEdit(true) }} onDelete={del} />

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => { setShowAdd(false); setNewName('') }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 360, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <IconBox icon="ti-mail" color={COLORS.blue} />
              <div><div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Add Email Category</div></div>
              <CloseBtn onClick={() => { setShowAdd(false); setNewName('') }} />
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 6 }}>Category Name</label>
            <input className="form-control" style={{ fontSize: 13 }} value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Appointment Booking" />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => { setShowAdd(false); setNewName('') }} style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}>Cancel</button>
              <button className="bgBtn btn-sm" style={{ padding: '7px 16px', fontSize: 13 }} onClick={async () => { await add(); setShowAdd(false) }}>Add</button>
            </div>
          </div>
        </div>
      )}

      {showEdit && editItem && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => { setShowEdit(false); setEditItem(null) }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 360, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <IconBox icon="ti-edit" color={COLORS.orange} />
              <div><div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Edit Email Category</div></div>
              <CloseBtn onClick={() => { setShowEdit(false); setEditItem(null) }} />
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 6 }}>Category Name</label>
            <input className="form-control" style={{ fontSize: 13 }} value={editItem.category_name}
              onChange={e => setEditItem({ ...editItem, category_name: e.target.value })} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => { setShowEdit(false); setEditItem(null) }} style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}>Cancel</button>
              <button className="bgBtn btn-sm" style={{ padding: '7px 16px', fontSize: 13 }} onClick={update}>Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── SMS tab ───────────────────────────────────────────────────────────────── */
const SmsTab = () => {
  const [categories, setCategories]          = useState([])
  const [loading, setLoading]                = useState(false)
  const [viewCategory, setViewCat]           = useState(null)
  const [newName, setNewName]                = useState('')
  const [editItem, setEditItem]              = useState(null)
  const [showAdd, setShowAdd]                = useState(false)
  const [showEdit, setShowEdit]              = useState(false)
  const [showReminderModal, setShowReminder] = useState(false)
  const [allTemplates, setAllTemplates]      = useState([])
  const [loadingTemplates, setLoadingTpl]    = useState(false)
  const [selectedTemplate, setSelectedTpl]  = useState(null)
  const [daysBefore, setDaysBefore]         = useState('')
  const [savingReminder, setSavingReminder] = useState(false)

  const saveReminder = async () => {
    if (!selectedTemplate || !daysBefore) return
    setSavingReminder(true)
    try {
      await axiosInstance.post('createAutomaticSms', { template_id: selectedTemplate, days_before: parseInt(daysBefore), type: 'appointment_reminder' })
      setShowReminder(false)
      setSelectedTpl(null)
      setDaysBefore('')
    } catch (e) { console.error(e) }
    finally { setSavingReminder(false) }
  }

  const openReminderModal = async () => {
    setShowReminder(true)
    setLoadingTpl(true)
    try {
      const [tplRes, existingRes] = await Promise.all([
        axiosInstance.get('getAllSmsTemplates'),
        axiosInstance.get('getAutomaticSms?type=appointment_reminder'),
      ])
      const list = Array.isArray(tplRes.data) ? tplRes.data : tplRes.data?.data || []
      setAllTemplates(list)
      const existing = existingRes.data?.data?.[0]
      if (existing) {
        setSelectedTpl(existing.template_id)
        setDaysBefore(String(existing.days_before))
      }
    } catch { setAllTemplates([]) }
    finally { setLoadingTpl(false) }
  }

  const fetch = async () => {
    setLoading(true)
    try {
      const res = await axiosInstance.get('getAllSmsPhoneCategories')
      setCategories(Array.isArray(res.data?.categories) ? res.data.categories : Array.isArray(res.data) ? res.data : [])
    } catch { } finally { setLoading(false) }
  }
  useEffect(() => { fetch() }, [])

  const add = async () => {
    if (!newName.trim()) return
    try { await axiosInstance.post(`${baseurl}addSmsPhoneCategory`, { category_name: newName.trim() }); setNewName(''); fetch() } catch { }
  }
  const update = async () => {
    if (!editItem?.category_name?.trim()) return
    try { await axiosInstance.put(`updateSmsPhoneCategory/${editItem.id}`, { category_name: editItem.category_name.trim() }); setEditItem(null); setShowEdit(false); fetch() } catch { }
  }
  const del = async (id) => {
    if (!window.confirm('Delete this SMS category?')) return
    try { await axiosInstance.delete(`deleteSmsPhoneCategory/${id}`); fetch() } catch { }
  }

  if (viewCategory) return <SmsTemplateListView category={viewCategory} onBack={() => setViewCat(null)} />

  return (
    <div style={{ padding: '14px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
        <button className="borderBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize: 13, padding: '5px 12px' }} onClick={openReminderModal}>
          <i className="ti ti-clock" style={{ fontSize: 15 }} /> Appointments Reminder
        </button>
        <button className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize: 13, padding: '5px 12px' }} onClick={() => setShowAdd(true)}>
          <i className="ti ti-plus" style={{ fontSize: 15 }} /> Add Category
        </button>
      </div>
      <CategoryPanel accentColor={COLORS.green} categories={categories} loading={loading}
        onView={setViewCat} onEdit={c => { setEditItem(c); setShowEdit(true) }} onDelete={del} />

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => { setShowAdd(false); setNewName('') }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 360, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <IconBox icon="ti-message" color={COLORS.green} />
              <div><div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Add SMS Category</div></div>
              <CloseBtn onClick={() => { setShowAdd(false); setNewName('') }} />
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 6 }}>Category Name</label>
            <input className="form-control" style={{ fontSize: 13 }} value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Appointment Reminders" />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => { setShowAdd(false); setNewName('') }} style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}>Cancel</button>
              <button className="bgBtn btn-sm" style={{ padding: '7px 16px', fontSize: 13 }} onClick={async () => { await add(); setShowAdd(false) }}>Add</button>
            </div>
          </div>
        </div>
      )}

      {showEdit && editItem && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => { setShowEdit(false); setEditItem(null) }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 360, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <IconBox icon="ti-edit" color={COLORS.orange} />
              <div><div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Edit SMS Category</div></div>
              <CloseBtn onClick={() => { setShowEdit(false); setEditItem(null) }} />
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 6 }}>Category Name</label>
            <input className="form-control" style={{ fontSize: 13 }} value={editItem.category_name}
              onChange={e => setEditItem({ ...editItem, category_name: e.target.value })} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => { setShowEdit(false); setEditItem(null) }} style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}>Cancel</button>
              <button className="bgBtn btn-sm" style={{ padding: '7px 16px', fontSize: 13 }} onClick={update}>Update</button>
            </div>
          </div>
        </div>
      )}

      {/* Appointments Reminder Modal */}
      {showReminderModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowReminder(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: 420, maxHeight: '75vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: '1px solid #e8ecf0' }}>
              <IconBox icon="ti-clock" color={COLORS.green} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Appointments Reminder</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>Select an SMS template to use</div>
              </div>
              <CloseBtn onClick={() => setShowReminder(false)} />
            </div>

            {/* Template list */}
            <div style={{ overflowY: 'auto', padding: '12px 20px', flex: 1 }}>
              {loadingTemplates ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: COLORS.gray, fontSize: 13 }}>Loading templates...</div>
              ) : allTemplates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: COLORS.gray }}>
                  <i className="ti ti-message-off" style={{ fontSize: 26, display: 'block', marginBottom: 6, opacity: 0.4 }} />
                  No templates found
                </div>
              ) : allTemplates.map((t, i) => (
                <label key={t.id ?? i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                  borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                  background: selectedTemplate === t.id ? `${COLORS.green}12` : 'transparent',
                  border: `1px solid ${selectedTemplate === t.id ? COLORS.green : '#e8ecf0'}`,
                  transition: 'all 0.15s',
                }}>
                  <input
                    type="radio"
                    name="reminderTemplate"
                    value={t.id}
                    checked={selectedTemplate === t.id}
                    onChange={() => setSelectedTpl(t.id)}
                    style={{ accentColor: COLORS.green, width: 16, height: 16, flexShrink: 0 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a202c' }}>{t.template_name || `Template ${i + 1}`}</div>
                    {t.content && (
                      <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t.content}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>

            {/* Days Before */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e8ecf0' }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 6 }}>Days Before</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={daysBefore}
                  onChange={e => setDaysBefore(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="e.g. 1"
                  style={{
                    width: 100, height: 36, borderRadius: 8, border: '1.5px solid #dde3eb',
                    padding: '0 12px', fontSize: 14, fontWeight: 600, color: '#1a202c',
                    outline: 'none', textAlign: 'center',
                  }}
                  onFocus={e => e.target.style.borderColor = COLORS.green}
                  onBlur={e => e.target.style.borderColor = '#dde3eb'}
                />
                <span style={{ fontSize: 13, color: COLORS.gray }}>day(s) before the appointment</span>
              </div>
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '12px 20px', borderTop: '1px solid #e8ecf0' }}>
              <button onClick={() => setShowReminder(false)} style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}>
                Cancel
              </button>
              <button className="bgBtn btn-sm" style={{ padding: '7px 16px', fontSize: 13 }} onClick={saveReminder} disabled={savingReminder}>
                {savingReminder ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Main component ────────────────────────────────────────────────────────── */
const NotificationCenter = () => {
  const navigate    = useNavigate()
  const [activeTab, setActiveTab] = useState('email')

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <BackBtn onClick={() => navigate(-1)} />
            <IconBox icon="ti-bell" color={COLORS.purple} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Notification Center</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Manage email and SMS notification templates</div>
            </div>
          </div>

          {/* Tab bar */}
          <div style={{ display: 'flex', gap: 4, padding: '10px 20px 0', borderBottom: '1px solid #e8ecf0', background: '#fafbfc' }}>
            {TABS.map(tab => {
              const active = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px',
                    fontSize: 13, fontWeight: active ? 700 : 500,
                    color: active ? tab.color : COLORS.gray,
                    background: active ? '#fff' : 'transparent',
                    border: '1px solid', borderColor: active ? '#e8ecf0' : 'transparent',
                    borderBottom: active ? `2px solid ${tab.color}` : '2px solid transparent',
                    borderRadius: '8px 8px 0 0', cursor: 'pointer',
                    marginBottom: -1, transition: 'all 0.15s',
                  }}
                >
                  <i className={`ti ${tab.icon}`} style={{ fontSize: 15 }} />
                  {tab.label}
                </button>
              )
            })}
          </div>

          {/* Tab content */}
          {activeTab === 'email' && <EmailTab />}
          {activeTab === 'sms'   && <SmsTab />}

        </div>
      </div>
    </div>
  )
}

export default NotificationCenter
